import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { CheckCircle2, ArrowRight, Sparkles, Zap, Globe, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useHubspotForm } from "@/hooks/useHubspotForm";
import { trackCTAClick, trackFormSubmit } from "@/lib/analytics";
import { toast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";

export default function Product360Landing() {
  useSEO({
    title: 'Product 360 - Unified Commerce Platform | Ommni',
    description: 'Craft your Product 360 with Ommni. Unify product information, pricing, and inventory in one intelligent platform. Built for scale, syndication, and speed.',
    keywords: 'product 360, product information management, pricing management, inventory management, commerce platform, omnichannel retail',
    canonical: 'https://ommni.io/product-360',
  });

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const { submitToHubspot, isSubmitting } = useHubspotForm("16df0eb1-7a54-4561-bd75-dabf2e80cc55");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !firstName || !company) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    trackFormSubmit("product360_landing", false);
    const success = await submitToHubspot({
      email,
      firstname: firstName,
      company,
      hs_lead_status: "NEW",
      message: "Product 360 Landing Page Lead",
    });

    if (success) {
      trackFormSubmit("product360_landing", true);
      setEmail("");
      setFirstName("");
      setCompany("");
      setShowSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Product 360 by Ommni",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Unified commerce platform that brings together product information, pricing, and inventory management into one intelligent decision engine.",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free demo available"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Single source of truth for all product records",
            "Dynamic pricing strategies by region and channel",
            "Real-time inventory across warehouses and stores",
            "OmmniFlow visual workflow engine",
            "Multi-channel synchronization",
            "100+ platform integrations"
          ],
          "screenshot": "https://ommni.io/assets/hero-commerce.jpg",
          "softwareVersion": "2024",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "150"
          }
        })}
      </script>
      {/* Floating Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/src/assets/ommni-logo.png" alt="Ommni" className="h-8" />
          </div>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                trackCTAClick("login", "product360_nav");
                window.location.href = "/auth";
              }}
            >
              Log In
            </Button>
            <Button 
              size="sm"
              onClick={() => {
                trackCTAClick("demo_nav", "product360_nav");
                document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Book Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Introducing Product 360</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Craft Your <span className="text-gradient bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">Product 360</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-medium">
            Define. Display. Distribute — Intelligently.
          </p>
          
          <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
            The Future of Product Data is Unified
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="text-lg px-8"
              onClick={() => {
                trackCTAClick("book_demo_hero", "product360_hero");
                document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-8"
              onClick={() => {
                trackCTAClick("download_overview", "product360_hero");
                window.location.href = "/contact";
              }}
            >
              Download Platform Overview
            </Button>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg leading-relaxed text-foreground mb-6">
            In today's commerce landscape, product data isn't just a back-office concern — it's your brand's first impression, your pricing power, and your ability to trade across every channel.
          </p>
          
          <p className="text-lg leading-relaxed text-foreground mb-8">
            Ommni's <strong>Product 360</strong> brings together the three pillars of retail operations:
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 bg-card border-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Product Information</h3>
              <p className="text-muted-foreground">Rich, structured, and channel-ready.</p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Pricing</h3>
              <p className="text-muted-foreground">Dynamic, multi-region, multi-currency, and rules-based.</p>
            </Card>

            <Card className="p-6 bg-card border-border hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <RefreshCw className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Inventory</h3>
              <p className="text-muted-foreground">Real-time, location-aware, and syndication-ready.</p>
            </Card>
          </div>

          <p className="text-xl font-semibold text-center mt-12 text-foreground">
            All in one platform. All working together.
          </p>
        </div>
      </section>

      {/* Why Product 360 */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">
            Why Product 360?
          </h2>
          
          <p className="text-lg text-muted-foreground mb-12 text-center max-w-3xl mx-auto">
            Ommni goes beyond traditional PIMs or middleware by tightly integrating product, pricing, and inventory management into one intelligent decision engine — designed for speed, scale, and syndication.
          </p>

          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 md:p-12 border border-border">
            <h3 className="text-2xl font-semibold mb-8">What you get:</h3>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg">A single source of truth for all product records — from SKUs to variants to meta content.</p>
              </div>
              
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg">Smarter pricing strategies that adapt by region, currency, or channel.</p>
              </div>
              
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg">Inventory that reflects real-world availability, across warehouses, stores, and suppliers.</p>
              </div>
              
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <p className="text-lg">Zero duplication, less rework, fewer errors.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Powered by OmmniFlow */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Powered by <span className="text-gradient bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">OmmniFlow</span>
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                Under the hood, every action is orchestrated by OmmniFlow — our visual workflow engine that automates and scales your product lifecycle.
              </p>
              
              <p className="text-lg font-semibold mb-6">Think of it as your operations brain:</p>
              
              <div className="space-y-4">
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <p>Automatically enrich and validate products as they arrive.</p>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <p>Apply pricing logic with business-specific rules.</p>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <p>Route inventory updates based on fulfilment priority.</p>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <p>Publish instantly to every connected system — Shopify, NetSuite, marketplaces, 3PLs, and more.</p>
                </div>
              </div>
            </div>
            
            <Card className="p-8 bg-gradient-to-br from-background to-muted border-border">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-lg">
                  <RefreshCw className="w-5 h-5 text-primary" />
                  <span className="font-medium">Visual Workflow Builder</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-accent/10 rounded-lg">
                  <Zap className="w-5 h-5 text-accent" />
                  <span className="font-medium">Smart Automation Rules</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-lg">
                  <Globe className="w-5 h-5 text-primary" />
                  <span className="font-medium">Multi-Channel Sync</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Built for Ecosystem */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Built for the Entire Ecosystem
          </h2>
          
          <p className="text-lg text-muted-foreground mb-12 max-w-3xl mx-auto">
            Whether you're scaling a DTC brand, running an omnichannel retail empire, or selling across B2B and B2C:
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-left hover:shadow-xl transition-shadow bg-card border-border">
              <Globe className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Multi-Location Aware</h3>
              <p className="text-muted-foreground">Store-level inventory & pricing intelligence.</p>
            </Card>

            <Card className="p-8 text-left hover:shadow-xl transition-shadow bg-card border-border">
              <Zap className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-3">Multi-Channel Ready</h3>
              <p className="text-muted-foreground">Shopify, NetSuite, Amazon, Marketplacer, and more.</p>
            </Card>

            <Card className="p-8 text-left hover:shadow-xl transition-shadow bg-card border-border">
              <RefreshCw className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Multi-Region Native</h3>
              <p className="text-muted-foreground">AUD/NZD/USD/GBP with localised pricing & fulfilment.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary/10 via-accent/5 to-background">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Intelligent Commerce Starts Here
          </h2>
          
          <p className="text-lg text-muted-foreground mb-8">
            Stop managing your product data in spreadsheets or stitching together siloed systems. With Ommni, you're not just launching a PIM — you're activating an intelligent commerce operating system.
          </p>

          <p className="text-xl font-semibold mb-12">
            Ready to Craft Your Product 360?
          </p>

          <Card className="p-8 md:p-12 max-w-2xl mx-auto bg-card border-border" id="demo-form">
            <h3 className="text-2xl font-bold mb-6">Get Started Today</h3>
            <p className="text-muted-foreground mb-8">
              Join retailers like Lorna Jane, Ringers Western, and Universal Coin & Bullion who are scaling smarter with Ommni.
            </p>
            
            {showSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h4 className="text-2xl font-bold mb-2">Thank You!</h4>
                <p className="text-lg text-muted-foreground mb-6">
                  Your demo request has been submitted successfully. We'll be in touch soon!
                </p>
                <Button 
                  variant="outline"
                  onClick={() => setShowSuccess(false)}
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder="First Name *"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="text-base"
                />
                <Input
                  type="email"
                  placeholder="Work Email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="text-base"
                />
                <Input
                  type="text"
                  placeholder="Company Name *"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  className="text-base"
                />
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Book Your Demo"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            )}

            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <button
                onClick={() => {
                  trackCTAClick("talk_to_sales", "product360_footer");
                  window.location.href = "/contact";
                }}
                className="hover:text-primary transition-colors"
              >
                Talk to Sales
              </button>
              <span>•</span>
              <button
                onClick={() => {
                  trackCTAClick("view_features", "product360_footer");
                  window.location.href = "/features";
                }}
                className="hover:text-primary transition-colors"
              >
                View All Features
              </button>
              <span>•</span>
              <button
                onClick={() => {
                  trackCTAClick("see_integrations", "product360_footer");
                  window.location.href = "/integrations";
                }}
                className="hover:text-primary transition-colors"
              >
                See Integrations
              </button>
            </div>
          </Card>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-8 px-6 border-t border-border/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>© 2024 Ommni. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="/support" className="hover:text-primary transition-colors">Support</a>
            <a href="/contact" className="hover:text-primary transition-colors">Contact</a>
            <a href="/" className="hover:text-primary transition-colors">Main Site</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
